<template>
  <div class="coupon-market-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('couponMarket.page.title')" :subtitle="$t('couponMarket.page.subtitle')" />

    <!-- (2) SearchPanel：供应商快捷筛选 + 名称搜索 -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.couponName" clearable
            :placeholder="$t('couponMarket.search.placeholder')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- 供应商快捷筛选条：保留原 supplierId 联动语义 -->
    <div class="ds-supplier-bar">
      <span class="ds-supplier-bar__label">{{ $t('couponMarket.supplier.label') }}</span>
      <el-button size="mini" :type="currentSupplierId === '' ? 'primary' : ''"
        @click="handleQuerySupplierCoupon({ supplierId: '' })">
        {{ $t('couponMarket.supplier.all') }}
      </el-button>
      <el-button v-for="item in couponMarketInfo.suppliers" :key="item.supplierId" size="mini"
        :type="currentSupplierId === item.supplierId ? 'primary' : ''" @click="handleQuerySupplierCoupon(item)">
        {{ item.supplierName }}
      </el-button>
    </div>

    <!-- 优惠券卡片网格 -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="loadCoupons">
        <template #left>
          <span class="ds-panel__title">{{ $t('couponMarket.list.title') }}</span>
        </template>
      </ds-toolbar>

      <div class="ds-coupon-grid">
        <div v-if="loading" class="ds-coupon-grid__loading" v-loading="true" element-loading-text="加载中"></div>

        <ds-error-state v-else-if="hasError" :description="errorMessage" @retry="loadCoupons" />

        <ds-empty-state v-else-if="couponMarketInfo.coupons.length === 0"
          :description="$t('couponMarket.page.empty')" />

        <div v-else class="ds-coupon-grid__list">
          <div v-for="(item, index) in couponMarketInfo.coupons" :key="index" class="ds-coupon-card">
            <!-- 券头：原页面为外链占位图，改为设计系统票券样式，纯装饰不影响数据 -->
            <div class="ds-coupon-card__head">
              <i class="el-icon-present el-icon-tickets"></i>
              <span class="ds-coupon-card__type">{{ $t('couponMarket.card.coupon') }}</span>
            </div>

            <div class="ds-coupon-card__body">
              <div class="ds-coupon-card__title" :title="item.name">{{ item.name || '-' }}</div>

              <div class="ds-coupon-card__price">
                <span class="ds-coupon-card__price-label">{{ $t('couponMarket.price') }}</span>
                <span class="ds-coupon-card__price-value ds-num">{{ formatMoney(item.valuePrice) }}</span>
              </div>

              <div class="ds-coupon-card__meta">
                <div class="ds-coupon-card__meta-row">
                  <span class="ds-coupon-card__meta-label">{{ $t('couponMarket.card.supplier') }}</span>
                  <span class="ds-coupon-card__meta-value">{{ item.supplierName || '-' }}</span>
                </div>
                <div class="ds-coupon-card__meta-row">
                  <span class="ds-coupon-card__meta-label">{{ $t('couponMarket.card.businessKey') }}</span>
                  <span class="ds-coupon-card__meta-value ds-num">{{ item.businessKey || '-' }}</span>
                </div>
                <div v-if="item.remark" class="ds-coupon-card__meta-row">
                  <span class="ds-coupon-card__meta-label">{{ $t('couponMarket.card.remark') }}</span>
                  <span class="ds-coupon-card__meta-value">{{ item.remark }}</span>
                </div>
              </div>
            </div>

            <div class="ds-coupon-card__footer">
              <span class="ds-coupon-card__id ds-num">{{ item.couponId || '-' }}</span>
              <el-link type="primary" class="ds-coupon-card__buy">{{ $t('couponMarket.buy') }}</el-link>
            </div>
          </div>
        </div>
      </div>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
        :page-sizes="[15, 30, 50]" @current-change="handlePageChange" />
    </div>
  </div>
</template>

<script>
import { listSupplier, listSupplierCoupon } from '@/api/scm/couponMarketApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsEmptyState from '@/components/ds/ds-empty-state.vue'
import DsErrorState from '@/components/ds/ds-error-state.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CouponMarketList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsEmptyState,
    DsErrorState,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      currentSupplierId: '',
      searchForm: {
        couponName: ''
      },
      couponMarketInfo: {
        coupons: [],
        suppliers: []
      },
      page: {
        current: 1,
        size: 15,
        // 后端 ResultVo(records=总页数, total=总条数)，此处 total 取真实总条数用于分页
        total: 0,
        pages: 1
      }
    }
  },
  created() {
    this.loadTopSupplier()
    this.loadCoupons()
  },
  methods: {
    async loadTopSupplier() {
      try {
        const params = {
          page: 1,
          row: 5
        }
        const res = await listSupplier(params)
        this.couponMarketInfo.suppliers = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.$message.error(this.$t('couponMarket.fetchSupplierError'))
      }
    },
    async loadCoupons() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          supplierId: this.currentSupplierId || ''
        }
        const res = await listSupplierCoupon(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.couponMarketInfo.coupons = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
        this.page.pages = res.records || 1
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('couponMarket.page.loadFailed')
        console.error('Failed to load coupon market:', error)
      } finally {
        this.loading = false
      }
    },
    handleQuerySupplierCoupon(item) {
      this.currentSupplierId = item.supplierId || ''
      this.page.current = 1
      this.loadCoupons()
    },
    handleSearch() {
      this.page.current = 1
      this.loadCoupons()
    },
    handleReset() {
      this.searchForm.couponName = ''
      this.currentSupplierId = ''
      this.page.current = 1
      this.loadCoupons()
    },
    handlePageChange(currentPage) {
      this.page.current = currentPage
      this.loadCoupons()
    },
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-market-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 供应商快捷筛选条 */
.ds-supplier-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--ds-spacing-sm);
  padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
  margin-bottom: var(--ds-spacing-md);
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);

  &__label {
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    margin-right: var(--ds-spacing-xs);
  }
}

/* 面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 优惠券卡片网格 */
.ds-coupon-grid {
  padding: var(--ds-spacing-lg);

  &__loading {
    min-height: 200px;
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: var(--ds-spacing-md);
  }
}

/* 单张券卡 */
.ds-coupon-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  background: #fff;
  overflow: hidden;
  transition: box-shadow var(--ds-transition-fast), transform var(--ds-transition-fast);

  &:hover {
    box-shadow: var(--ds-shadow-md);
    transform: translateY(-2px);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-xs);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    color: #fff;
    font-size: var(--ds-font-size-xs);
    background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-hover) 100%);
  }

  &__body {
    flex: 1;
    padding: var(--ds-spacing-md);
  }

  &__title {
    font-size: var(--ds-font-size-base);
    font-weight: 600;
    color: var(--ds-gray-800);
    margin-bottom: var(--ds-spacing-sm);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__price {
    display: flex;
    align-items: baseline;
    gap: var(--ds-spacing-xs);
    margin-bottom: var(--ds-spacing-sm);
  }

  &__price-label {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
  }

  &__price-value {
    font-size: var(--ds-font-size-xl);
    font-weight: 700;
    color: var(--ds-danger);
    font-family: var(--ds-font-family-num);
    font-variant-numeric: tabular-nums;
  }

  &__meta-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--ds-spacing-sm);
    padding: 3px 0;
    font-size: var(--ds-font-size-xs);
  }

  &__meta-label {
    color: var(--ds-gray-500);
    flex-shrink: 0;
  }

  &__meta-value {
    color: var(--ds-gray-700);
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    border-top: 1px dashed var(--ds-gray-200);
    background: var(--ds-gray-50);
  }

  &__id {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__buy {
    flex-shrink: 0;
    font-weight: 600;
  }
}
</style>
